import React, { useState } from 'react';
import { ExternalLink, Loader2, Lock, ShieldAlert, X } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { openSecureFile, isGrantRequired } from '../../utils/secureFile';
import { toast } from '../ui/sonner';

// Break-glass dialog: HR states a reason → 15-minute audited grant → file opens.
export function AccessRequestDialog({ workerId, documentId, fileLabel, onGranted, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 10) { toast.error('Please give a reason of at least 10 characters'); return; }
    setBusy(true);
    try {
      const r = await apiCall('/api/files/access-request', { method: 'POST', body: JSON.stringify({ worker_id: workerId, document_id: documentId || null, reason: reason.trim() }) });
      toast.success(`Access granted for ${r.ttl_minutes} minutes — the worker and platform admins have been notified`);
      onGranted?.(r);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4" data-testid="access-request-dialog">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-amber-500 text-white p-4 flex items-start justify-between">
          <div className="flex items-center gap-2"><ShieldAlert className="w-5 h-5" /><div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-amber-100">Restricted document</p>
            <h3 className="font-bold">Request access</h3></div></div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20" data-testid="access-request-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{fileLabel || 'This file'}</span> is an identity / restricted document. Opening it is logged with your name, reason and IP address, and the worker is notified. Access lasts 15 minutes.
          </p>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={500} autoFocus
            placeholder="Why do you need to see this document? e.g. 'Verifying ID for onboarding paperwork'"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
            data-testid="access-request-reason" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="access-request-cancel">Cancel</button>
            <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50" data-testid="access-request-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Grant me access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Replaces raw <a href="/api/uploads/..."> everywhere: signs a short-lived link, handles restricted docs.
export function SecureFileLink({ path, workerId, documentId, label = 'View file', fileLabel, sensitivity, className, testId, children }) {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const restricted = sensitivity === 'restricted';
  const open = async () => {
    if (!path) return;
    setBusy(true);
    try { await openSecureFile(path); }
    catch (e) {
      if (isGrantRequired(e) && workerId) setAsk(true);
      else toast.error(e.message);
    }
    setBusy(false);
  };
  return (
    <>
      <button type="button" onClick={open} disabled={busy}
        className={className || 'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60'}
        data-testid={testId || 'secure-file-link'} title={restricted ? 'Restricted — access is logged and requires a reason' : 'Opens a 10-minute secure link (logged)'}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : restricted ? <Lock className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
        {children || label}
      </button>
      {ask && (
        <AccessRequestDialog workerId={workerId} documentId={documentId} fileLabel={fileLabel}
          onClose={() => setAsk(false)}
          onGranted={async () => { setAsk(false); try { await openSecureFile(path); } catch (e) { toast.error(e.message); } }} />
      )}
    </>
  );
}
